import CarRepairIcon from "@mui/icons-material/CarRepair";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import SvgIcon from "@mui/material/SvgIcon";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import React from "react";
import { ModalType, useModal } from "@/components/ModalProvider";
import { MAINTENANCE_SOURCE } from "@/constants/MaintenanceSource";
import { DisplayMaintenance } from "@/models/MaintenanceDisplayData";
import SteeringWheelIcon from "@assets/steering_wheel.svg?react";

/** Map the highest severity in a set of jobs to an icon color */
function severityToMuiColor(maxSeverity?: number): "inherit" | "primary" | "warning" | "error" {
  if (maxSeverity == null) return "inherit";
  // <=100 -> primary, <=300 -> warning, else -> error
  return maxSeverity <= 100 ? "primary" : maxSeverity <= 300 ? "warning" : "error";
}

export function MaintenanceCell(props: GridRenderCellParams) {
  const maintenance: DisplayMaintenance = props.value;
  const { openModal } = useModal();

  const jobs = maintenance?.workOrderJobs ?? [];
  const totalCount = jobs.length;

  // Partition jobs
  const ecoOpen = jobs.filter(
    wj => wj?.maintenanceType?.source === MAINTENANCE_SOURCE.ECO_INSPECTION && wj?.statusCode === 1,
  );
  const ecoOnHold = jobs.filter(
    wj => wj?.maintenanceType?.source === MAINTENANCE_SOURCE.ECO_INSPECTION && wj?.statusCode === 4,
  );
  const ecoActive = jobs.filter(
    wj =>
      wj?.maintenanceType?.source === MAINTENANCE_SOURCE.ECO_INSPECTION &&
      [2, 3].includes(wj?.statusCode ?? 0),
  );
  const others = jobs.filter(
    wj => wj?.maintenanceType?.source !== MAINTENANCE_SOURCE.ECO_INSPECTION,
  );

  // Highest severity per class (for icon color)
  const ecoOpenMaxSeverity = ecoOpen.length
    ? Math.max(...ecoOpen.map(j => j?.severityCode ?? 0))
    : undefined;
  const ecoActiveMaxSeverity = ecoActive.length
    ? Math.max(...ecoActive.map(j => j?.severityCode ?? 0))
    : undefined;
  const othersMaxSeverity = others.length
    ? Math.max(...others.map(j => j?.severityCode ?? 0))
    : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: "100%",
        alignItems: "center",
        m: "auto",
      }}
      onClick={() => {
        if (maintenance?.workOrderJobs || maintenance?.workOrders) {
          openModal(maintenance, ModalType.MAINTENANCE_DETAILS);
        }
      }}
    >
      {totalCount > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* ECO open -> Steering wheel */}
          {ecoOpen.length > 0 && (
            <Badge badgeContent={ecoOpen.length} color="primary" overlap="circular">
              <SvgIcon
                component={SteeringWheelIcon}
                inheritViewBox
                fontSize="medium"
                color={severityToMuiColor(ecoOpenMaxSeverity)}
                sx={{ "& path": { fill: "currentColor" } }}
              />
            </Badge>
          )}

          {/* ECO on hold (Vorschaden) -> grey steering wheel */}
          {ecoOnHold.length > 0 && (
            <Badge badgeContent={ecoOnHold.length} color="primary" overlap="circular">
              <SvgIcon
                component={SteeringWheelIcon}
                inheritViewBox
                fontSize="medium"
                sx={{ "& path": { fill: "currentColor" }, color: "grey.500" }}
              />
            </Badge>
          )}

          {/* ECO active -> CarRepair */}
          {ecoActive.length > 0 && (
            <Badge badgeContent={ecoActive.length} color="primary" overlap="circular">
              <CarRepairIcon
                sx={{ display: "block" }}
                color={severityToMuiColor(ecoActiveMaxSeverity)}
                fontSize="medium"
              />
            </Badge>
          )}

          {/* Non-ECO -> Info icon */}
          {others.length > 0 && (
            <Badge badgeContent={others.length} color="primary" overlap="circular">
              <InfoOutlinedIcon
                sx={{ display: "block" }}
                color={severityToMuiColor(othersMaxSeverity)}
                fontSize="medium"
              />
            </Badge>
          )}
        </Box>
      )}
    </Box>
  );
}
